/* */

import React, { Fragment, useCallback, useRef, useState } from 'react';
import { components } from 'react-select';
import { FormFeedback } from 'reactstrap';
import { cn } from 'helpers/css';
import { stylesForRemove } from 'helpers/select';
import { get, isArray, isNumber, isPlainObject, isString } from 'lodash';
import Select, { AsyncSelect } from 'components/Select';
import usePagingSelect from 'hooks/usePagingSelect';
import { isDefine, isReactComponent } from 'helpers';
import { Check, PlusCircle, XCircle } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'shadcn/components/command';
import { Skeleton } from 'shadcn/components/skeleton';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/popover';
import NewDesign from 'components/NewDesign';
import { Badge } from 'shadcn/components/badge';
import { Separator } from 'shadcn/components/separator';
import Loading from 'components/Loading';
import SelectOptions from 'components/Select/v2/SelectOptions';
import SelectFetchingData from 'components/Select/SelectFetchingData';

const CustomOption = (props) => {
  const { innerProps, isDisabled, children, isSelected } = props;
  const { onMouseMove, onMouseOver, ...newInnerProps } = innerProps;
  if (isDisabled) {
    return null;
  }
  return (
    <div {...newInnerProps} className={`select-custom-dzb ${isSelected ? 'is-active' : ''}`}>
      {children}
    </div>
  );
};

const MultiValueLabelDefault = (props) => (
  <components.MultiValueLabel {...props}>{props.data.label}</components.MultiValueLabel>
);

const AsyncSelectField = (props) => {
  const {
    label = '',
    isMulti = false,
    cacheOptions = false,
    defaultOptions = [],
    loadOptions = () => {},
    field,
    form,
    placeholder,
    noOptionsMessage,
    menuPortalTarget,
    afterOnChange = (val, { action, removedValue }) => {},
    afterSelectObj = (valSelected, { action, removedValue }) => {},
    CustomMultiValueLabel,
    isDisabled,

    // For custom error
    errorField,
    styles = {},
    onPaste = () => {},
    paging = false,
    onMenuScrollToBottom = () => {},
    required = false,
    className = '',
    closeMenuOnSelect = true,
    closeMenuOnScroll = true,
    isLabelComponent,
    afterLabel,
    menuIsOpen,

    isClearable,
    isCopyAble = false,
    hideCloseIcon = false,
    isQuickView = false,
    customQuickView,
    onClickQuickView,
    onClickRedirectPage,

    hideSelectedOptions,
    extraProps,
    classNameContainer,
    v2,
    numberOfBadge = 2,
    disabled,
  } = props;
  const [open, setOpen] = useState(false);

  const { name, value } = field || {};
  const { touched, errors, setFieldValue, setFieldTouched } = form || {};
  const rootRef = useRef(false);

  const error = get(errors, name);
  const touch = get(touched, name);
  const isInvalid = errorField || (touch && !!error);

  //* To prevent confusion between the old and new versions
  const isSelectDisabled = disabled || isDisabled;

  //* Use for scroll to next page
  const {
    options,
    loading,
    onChangeValuePaging,
    onMenuScrollToBottomPaging,
    onMenuOpenPaging,
    onMenuClosePaging,
  } = usePagingSelect(defaultOptions, loadOptions);

  //! Function
  const renderError = useCallback(() => {
    if (errorField && isString(errorField)) {
      return <FormFeedback>{errorField}</FormFeedback>;
    }

    if (touch) {
      if (!!error && isString(error)) {
        return <FormFeedback>{error}</FormFeedback>;
      }
    }

    return null;
  }, [errorField, error, touch]);

  const onPasteCallback = useCallback(
    (e) => {
      onPaste && onPaste({ event: e, ref: rootRef, propsFormik: form });
    },
    [rootRef, form]
  );

  const handleChangeSelect = (val, { action, removedValue }) => {
    if (isMulti) {
      switch (action) {
        case 'remove-value':
        case 'pop-value':
          if (removedValue && removedValue.isFixed) {
            return;
          }
          break;
        case 'clear':
          setFieldValue &&
            setFieldValue(
              name,
              value.filter((v) => v.isFixed)
            );
          afterOnChange(
            value.filter((v) => v.isFixed),
            { action, removedValue }
          );
          afterSelectObj(val?.[val.length - 1] || {}, { action, removedValue });
          return;
      }
      setFieldValue && setFieldValue(name, val || []);
      afterSelectObj(val?.[val.length - 1] || {}, { action, removedValue });
      afterOnChange(val, { action, removedValue });
    } else {
      setFieldValue && setFieldValue(name, val);
      afterSelectObj(val || {}, { action, removedValue });
      afterOnChange(val, { action, removedValue });
    }
  };

  const handleSelectItemV2 = (option, isSelected) => {
    if (isMulti) {
      let nextSelectOption = value || [];

      if (isSelected) {
        nextSelectOption = nextSelectOption.filter((el) => el.value !== option.value);
      } else {
        nextSelectOption = [...nextSelectOption, option];
      }

      setFieldValue(name, nextSelectOption);
      afterOnChange && afterOnChange(nextSelectOption, {});
    } else {
      setFieldValue(name, option);
      afterOnChange && afterOnChange(option, {});
      setOpen(false);
    }
  };

  //! Render
  const renderLabel = () => {
    if (!label) {
      return null;
    }

    return (
      <Fragment>
        {isLabelComponent && label}
        {!isLabelComponent && (
          <label
            className={cn(required ? 'required' : '', '!tw-mb-1')}
            dangerouslySetInnerHTML={isReactComponent(label) ? undefined : { __html: label }}
          >
            {isReactComponent(label) ? label : undefined}
          </label>
        )}
      </Fragment>
    );
  };

  if (paging) {
    if (v2) {
      const renderValue = () => {
        if (!isDefine(value)) {
          return null;
        }

        return (
          <Fragment>
            <Separator orientation='vertical' className='tw-mx-0.5 tw-h-4' />
            {isArray(value) ? (
              value?.length > numberOfBadge ? (
                <Badge variant='secondary' className='tw-rounded-sm tw-px-1 tw-font-extrabold'>
                  {value?.length} selected
                </Badge>
              ) : (
                value.map((v, index) => {
                  return (
                    <Badge
                      key={`${index}`}
                      variant='secondary'
                      className='tw-rounded-sm tw-px-1 tw-font-extrabold'
                    >
                      <p className='tw-max-w-[80px] tw-truncate'>{v?.label}</p>
                    </Badge>
                  );
                })
              )
            ) : null}

            {isPlainObject(value) && (
              <Badge variant='secondary' className='tw-rounded-sm tw-px-1 tw-font-extrabold'>
                {value?.label}
              </Badge>
            )}
          </Fragment>
        );
      };

      return (
        <div className={cn('component:SelectField', className)}>
          <Popover
            open={open && !isSelectDisabled}
            onOpenChange={(openState) => {
              if (openState === true) {
                onMenuOpenPaging();
              } else {
                setTimeout(() => {
                  onMenuClosePaging();
                }, 500);
              }

              setOpen(openState);
            }}
          >
            <PopoverTrigger className='tw-w-full tw-min-h-9'>
              <NewDesign.Button
                variant='outline'
                className={cn(
                  'tw-w-full tw-border-dashed !tw-min-h-9 tw-h-auto tw-py-[6px] tw-justify-start dark:tw-bg-primary-darkInput',
                  isInvalid && 'tw-border-red-500 tw-text-red-500',
                  isSelectDisabled && 'tw-bg-gray-100 dark:tw-opacity-50 !tw-cursor-default'
                )}
              >
                {(isMulti ? value?.length > 0 : isDefine(value?.value)) && isClearable ? (
                  <div
                    role='button'
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (isString(value) || isNumber(value)) {
                        setFieldValue(name, '');
                      }

                      if (isPlainObject(value)) {
                        setFieldValue(name, undefined);
                      }

                      if (isArray(value)) {
                        setFieldValue(name, []);
                      }
                    }}
                    className={cn(
                      'tw-rounded-sm tw-opacity-70 tw-transition-opacity tw-hover:opacity-100 tw-focus-visible:outline-none tw-focus-visible:ring-1 tw-focus-visible:ring-ring',
                      isInvalid && 'tw-text-red-500',
                      'tw-flex tw-items-center'
                    )}
                  >
                    <XCircle />
                  </div>
                ) : (
                  <div className='tw-flex tw-items-center'>
                    <PlusCircle />
                  </div>
                )}
                <div className='tw-ml-1'>{label}</div>

                {renderValue()}
              </NewDesign.Button>

              {renderError()}
            </PopoverTrigger>

            <PopoverContent className='tw-w-max tw-p-0 tw-max-w-[500px]' align='start'>
              <SelectOptions
                isMulti={isMulti}
                onChangeSelect={handleSelectItemV2}
                options={options}
                selected={value}
                isLoading={loading}
                onChangeInputSearch={(value) => {
                  onChangeValuePaging(value, { action: 'input-change' });
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    return (
      <div className={cn('component:AsyncSelectField', classNameContainer, className)}>
        {renderLabel()}
        {afterLabel}
        <div onPaste={onPasteCallback} ref={rootRef}>
          <Select
            name={name}
            id={name}
            isLoading={loading}
            className={cn({
              'is-invalid-input-select': isInvalid,
            })}
            value={value || props.value || null}
            isMulti={isMulti}
            options={options.filter((el) => {
              if (isArray(value)) {
                const itemsFixed = value.filter((v) => v.isFixed);

                if (isArray(itemsFixed) && itemsFixed.length > 0) {
                  return !itemsFixed.map((itemFixed) => itemFixed.value).includes(el.value);
                }
              }

              return true;
            })}
            placeholder={placeholder}
            onChange={handleChangeSelect}
            noOptionsMessage={noOptionsMessage}
            components={{
              Option: CustomOption,
              MultiValueLabel: CustomMultiValueLabel || MultiValueLabelDefault,
            }}
            styles={{ ...styles, ...stylesForRemove }}
            menuPortalTarget={menuPortalTarget}
            isDisabled={isSelectDisabled}
            menuPlacement='auto'
            classNamePrefix='react-select'
            onInputChange={onChangeValuePaging}
            onMenuScrollToBottom={onMenuScrollToBottomPaging}
            onMenuOpen={onMenuOpenPaging}
            onMenuClose={() => {
              onMenuClosePaging();
              setFieldTouched && setFieldTouched(name, true);
            }}
            hideSelectedOptions={isMulti ? true : hideSelectedOptions || false}
            closeMenuOnSelect={isMulti ? false : closeMenuOnSelect}
            closeMenuOnScroll={closeMenuOnScroll}
            menuIsOpen={menuIsOpen}
            isQuickView={isQuickView}
            isCopyAble={isCopyAble}
            hideCloseIcon={hideCloseIcon}
            isClearable={
              isQuickView ||
              isCopyAble ||
              isClearable ||
              (value && isArray(value) && value.some((v) => !v.isFixed))
            }
            customQuickView={customQuickView}
            onClickQuickView={onClickQuickView}
            onClickRedirectPage={onClickRedirectPage}
            extraProps={extraProps}
            loadingMessage={() => {
              return <SelectFetchingData />;
            }}
          />
          {renderError()}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('component:AsyncSelectField', classNameContainer, className)}>
      {renderLabel()}
      {afterLabel}
      <div onPaste={onPasteCallback} ref={rootRef}>
        <AsyncSelect
          name={name}
          id={name}
          className={cn('component:AsyncSelectField', {
            'is-invalid-input-select': isInvalid,
          })}
          value={value}
          isMulti={isMulti}
          cacheOptions={cacheOptions}
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          placeholder={placeholder}
          onChange={handleChangeSelect}
          noOptionsMessage={noOptionsMessage}
          isClearable={isClearable || (value && isArray(value) && value.some((v) => !v.isFixed))}
          components={{
            Option: CustomOption,
            MultiValueLabel: CustomMultiValueLabel || MultiValueLabelDefault,
          }}
          styles={{ ...styles, ...stylesForRemove }}
          menuPortalTarget={menuPortalTarget}
          isDisabled={isSelectDisabled}
          menuPlacement='auto'
          classNamePrefix='react-select'
          onMenuScrollToBottom={onMenuScrollToBottom}
          closeMenuOnSelect={closeMenuOnSelect}
          closeMenuOnScroll={closeMenuOnScroll}
          isCopyAble={isCopyAble}
          hideCloseIcon={hideCloseIcon}
          customQuickView={customQuickView}
          onClickQuickView={onClickQuickView}
          hideSelectedOptions={isMulti ? true : hideSelectedOptions}
          extraProps={extraProps}
        />
        {renderError()}
      </div>
    </div>
  );
};

export default AsyncSelectField;
